// Filter Component
import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export type FilterFieldType = 'input' | 'textarea' | 'dropdown' | 'date' | 'time' | 'date-time' | 'multi-select' | 'radio' | 'checkbox' | 'searchable-dropdown' | 'practice-location' | 'find-button' | 'reset-button' | 'find-reset-buttons';

export interface FilterField {
  type: FilterFieldType;
  key: string;
  label: string;
  placeholder?: string;
  options?: { label: string; value: string | number }[];
  config?: any; // Additional config for specific field types
  // For searchable-dropdown
  searchableOptions?: { label: string; value: string | number; displayLabel?: string }[];
  isLoading?: boolean;
}

export interface FilterConfig {
  fields: FilterField[];
  layout?: 'horizontal' | 'vertical' | 'grid';
  columns?: number; // For grid layout
  gridRows?: number; // When using grid with explicit placement (e.g. 4 rows)
  showReset?: boolean;
  showApply?: boolean;
  applyLabel?: string;
  /** When > 0, debounces `filterChange` emissions (reduces jank from date pickers and fast typing). Apply/Reset still read the form directly. */
  filterChangeDebounceMs?: number;
  /**
   * With `layout: 'grid'`, place Apply/Reset in a specific cell instead of a full-width bottom row.
   * Omit to keep the default: last row, full width, aligned end.
   */
  actionButtonsGrid?: { gridRow: number; gridColumn: number; colSpan?: number };
}

@Component({
  selector: 'app-filter',
  standalone: false,
  templateUrl: './filter.component.html',
  styleUrl: './filter.component.css'
})
export class FilterComponent implements OnInit, OnDestroy, OnChanges {
  @Input() config!: FilterConfig;
  @Input() fields: FilterField[] = [];
  @Input() layout: 'horizontal' | 'vertical' | 'grid' = 'horizontal';
  @Input() columns: number = 3;
  @Input() showReset: boolean = true;
  @Input() showApply: boolean = true;
  @Input() applyLabel: string = 'Apply';
  /** When true, Apply/Search button shows a spinner and is disabled (parent drives during API load). */
  @Input() applyLoading: boolean = false;
  /** When true, Find button shows a spinner and is disabled (parent drives during API load). */
  @Input() findLoading: boolean = false;
  /** When true, Reset button shows a spinner and is disabled (parent drives during refresh after reset). */
  @Input() resetLoading: boolean = false;
  @Input() visibleFieldsCount: number = 0;

  @Output() filterChange = new EventEmitter<any>();
  @Output() filterReset = new EventEmitter<void>();
  @Output() filterApply = new EventEmitter<any>();
  @Output() filterFind = new EventEmitter<void>();
  /** Emitted when an input field with `config.showEditIcon` triggers its edit (e.g. Batch #). */
  @Output() filterFieldEdit = new EventEmitter<string>();
  @Output() searchQuery = new EventEmitter<{ fieldKey: string; searchTerm: string }>();
  /** Emitted when a searchable dropdown input receives focus (parent can load initial options). */
  @Output() searchableDropdownFocus = new EventEmitter<string>();

  filterForm!: FormGroup;
  private isResetting: boolean = false;
  private ssnFormatSub: Subscription | null = null;
  private filterValueChangesSub: Subscription | null = null;
  private dateRangePairs: Array<{ fromKey: string; toKey: string }> = [];
  private dateRangeSubs: Subscription[] = [];
  private searchableDisplayLabels: Record<string, string> = {};

  /** Snapshot display labels for searchable dropdowns (e.g. provider name for stored id). */
  getSearchableDisplayLabelsSnapshot(): Record<string, string> {
    return { ...this.searchableDisplayLabels };
  }

  /** Restore display labels after navigation/overlay return. Pass `null` to clear all labels on reset. */
  restoreSearchableDisplayLabels(labels: Record<string, string> | null | undefined): void {
    if (labels === null) {
      this.searchableDisplayLabels = {};
    } else if (labels && typeof labels === 'object') {
      this.searchableDisplayLabels = { ...this.searchableDisplayLabels, ...labels };
    }
    this.cdr.markForCheck();
  }

  /** Clear searchable dropdown values, labels, and cached options (parent-driven reset). */
  clearSearchableFields(fieldKeys?: string[]): void {
    const fields = this.config?.fields || this.fields || [];
    const keys =
      fieldKeys ??
      fields.filter((field) => field.type === 'searchable-dropdown').map((field) => field.key);

    keys.forEach((key) => {
      delete this.searchableDisplayLabels[key];
      const field = fields.find((f) => f.key === key);
      if (field) {
        field.searchableOptions = [];
      }
      this.setSearchableSelection(key, '', undefined, false);
    });
    this.cdr.markForCheck();
  }

  /** Filter keys that should be trimmed before emitting values to parent. */
  private get trimEligibleKeys(): Set<string> {
    const fields = this.config?.fields || this.fields || [];
    return new Set(
      fields
        .filter((f) => f.type === 'input' || f.type === 'textarea')
        .map((f) => f.key)
    );
  }

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef) { }

  ngOnInit(): void {
    this.initializeForm();
    this.setupSsnFormatting();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const cfg = changes['config'];
    const flds = changes['fields'];
    if (!cfg && !flds) return;
    // Rebuild controls when parent replaces filter config/fields (same reference mutation never fires — see callers).
    const configUpdated = cfg && !cfg.firstChange;
    const fieldsUpdated = flds && !flds.firstChange;
    if (!configUpdated && !fieldsUpdated) return;
    if (!this.filterForm) return;
    this.ssnFormatSub?.unsubscribe();
    this.ssnFormatSub = null;
    this.filterValueChangesSub?.unsubscribe();
    this.dateRangeSubs.forEach((s) => s.unsubscribe());
    this.dateRangeSubs = [];
    this.initializeForm();
    this.setupSsnFormatting();
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.ssnFormatSub?.unsubscribe();
    this.filterValueChangesSub?.unsubscribe();
    this.dateRangeSubs.forEach((s) => s.unsubscribe());
    this.dateRangeSubs = [];
  }

  /** Format SSN as XXX-XX-XXXX as the user types. */
  private setupSsnFormatting(): void {
    this.ssnFormatSub?.unsubscribe();
    this.ssnFormatSub = null;
    const control = this.filterForm?.get('ssn') as AbstractControl | null;
    if (!control) return;
    this.ssnFormatSub = control.valueChanges.subscribe((value: string | number) => {
      if (value == null || value === '') return;
      const str = String(value);
      const digits = str.replace(/\D/g, '').slice(0, 9);
      if (digits.length === 0) {
        control.setValue('', { emitEvent: false });
        return;
      }
      let formatted: string;
      if (digits.length <= 3) {
        formatted = digits;
      } else if (digits.length <= 5) {
        formatted = digits.slice(0, 3) + '-' + digits.slice(3);
      } else {
        formatted = digits.slice(0, 3) + '-' + digits.slice(3, 5) + '-' + digits.slice(5);
      }
      if (formatted !== str) {
        control.setValue(formatted, { emitEvent: false });
      }
    });
  }

  private getFieldDefaultValue(field: FilterField): any {
    if (field.type === 'checkbox') {
      return field.config?.defaultValue === true;
    }
    if (field.config?.defaultValue !== undefined && field.config?.defaultValue !== null) {
      return field.config.defaultValue;
    }
    return '';
  }

  private initializeForm(): void {
    this.ssnFormatSub?.unsubscribe();
    this.ssnFormatSub = null;
    this.filterValueChangesSub?.unsubscribe();
    this.dateRangeSubs.forEach((s) => s.unsubscribe());
    this.dateRangeSubs = [];

    const formControls: any = {};
    const fields = this.config?.fields || this.fields;

    fields.forEach(field => {
      if (field.type === 'reset-button' || field.type === 'find-button' || field.type === 'find-reset-buttons') return;

      const defaultValue = this.getFieldDefaultValue(field);
      const isRequired = field.config?.required || false;

      if (field.type === 'date-time') {
        formControls[field.key + '_date'] = [''];
        formControls[field.key + '_time'] = [''];
      } else if (field.type === 'practice-location') {
        formControls[field.key + '_location'] = [''];
        formControls[field.key + '_practice'] = [{ value: '', disabled: false }];
      } else {
        const validators: ValidatorFn[] = [];
        if (isRequired) validators.push(Validators.required);
        if (Array.isArray(field.config?.validators)) validators.push(...field.config.validators);
        formControls[field.key] = [defaultValue, validators.length ? validators : []];
      }
    });

    this.filterForm = this.fb.group(formControls);
    this.dateRangePairs = this.buildDateRangePairs(fields);
    // Ensure default values are also normalized (from <= to) at startup.
    this.enforceDateRangeOrder(this.filterForm.value);
    this.setupImmediateDateRangeAutoFix();

    this.filterValueChangesSub?.unsubscribe();
    const debounceMs = this.config?.filterChangeDebounceMs;
    const raw$ = this.filterForm.valueChanges;
    const stream$ =
      debounceMs != null && debounceMs > 0
        ? raw$.pipe(
            debounceTime(debounceMs),
            distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
          )
        : raw$;

    this.filterValueChangesSub = stream$.subscribe(values => {
      const normalizedValues = this.enforceDateRangeOrder(values);
      if (!this.isResetting) {
        this.filterChange.emit(this.sanitizeValuesForEmit(normalizedValues));
      }
    });
  }

  /** Trim only text-entered filter values (input / textarea) before parent consumes them. */
  private sanitizeValuesForEmit(values: any): any {
    if (!values || typeof values !== 'object') return values;
    const keys = this.trimEligibleKeys;
    if (keys.size === 0) return values;
    const out: any = { ...values };
    keys.forEach((k) => {
      const v = out[k];
      if (typeof v === 'string') {
        out[k] = v.trim();
      }
    });
    return out;
  }

  private buildDateRangePairs(fields: FilterField[]): Array<{ fromKey: string; toKey: string }> {
    const dateKeys = fields
      .filter((f) => f.type === 'date')
      .map((f) => f.key);
    const dateKeySet = new Set(dateKeys);
    const pairs: Array<{ fromKey: string; toKey: string }> = [];
    const seen = new Set<string>();

    dateKeys.forEach((key) => {
      const candidates = this.getToKeyCandidates(key);
      if (candidates.length === 0) return;
      const candidate = candidates.find((c) => dateKeySet.has(c));
      if (!candidate) return;
      const pairKey = `${key}=>${candidate}`;
      if (seen.has(pairKey)) return;
      seen.add(pairKey);
      pairs.push({ fromKey: key, toKey: candidate });
    });

    return pairs;
  }

  /**
   * Generate possible "to" key names from a given "from" key while preserving common casing styles.
   * Examples:
   * - apptFrom -> apptTo
   * - visitDateFrom -> visitDateTo
   * - entry_from -> entry_to
   * - fromDate -> toDate
   */
  private getToKeyCandidates(fromKey: string): string[] {
    if (!fromKey) return [];
    const out: string[] = [];
    const add = (x: string) => {
      if (x && x !== fromKey && !out.includes(x)) out.push(x);
    };

    // Tail replacements first (most common naming in this repo)
    if (/From$/.test(fromKey)) add(fromKey.replace(/From$/, 'To'));
    if (/from$/.test(fromKey)) add(fromKey.replace(/from$/, 'to'));
    if (/FromDate$/.test(fromKey)) add(fromKey.replace(/FromDate$/, 'ToDate'));
    if (/fromDate$/.test(fromKey)) add(fromKey.replace(/fromDate$/, 'toDate'));
    if (/_from$/.test(fromKey)) add(fromKey.replace(/_from$/, '_to'));
    if (/-from$/.test(fromKey)) add(fromKey.replace(/-from$/, '-to'));
    if (/_fromDate$/.test(fromKey)) add(fromKey.replace(/_fromDate$/, '_toDate'));
    if (/-fromDate$/.test(fromKey)) add(fromKey.replace(/-fromDate$/, '-toDate'));

    // Prefix style: fromDate -> toDate
    if (/^From/.test(fromKey)) add(fromKey.replace(/^From/, 'To'));
    if (/^from/.test(fromKey)) add(fromKey.replace(/^from/, 'to'));
    if (/^from_/.test(fromKey)) add(fromKey.replace(/^from_/, 'to_'));
    if (/^from-/.test(fromKey)) add(fromKey.replace(/^from-/, 'to-'));

    // Fallback generic replace (kept last)
    add(fromKey.replace(/from/i, 'to'));

    return out;
  }

  private normalizeDateOnly(d: Date): Date {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
  }

  private parseDateLike(value: any): Date | null {
    if (value == null || value === '') return null;
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : this.normalizeDateOnly(value);
    }
    if (typeof value !== 'string') return null;
    const s = value.trim();
    if (!s) return null;

    // yyyy-mm-dd
    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso) {
      const y = parseInt(iso[1], 10);
      const m = parseInt(iso[2], 10) - 1;
      const d = parseInt(iso[3], 10);
      const date = new Date(y, m, d);
      return isNaN(date.getTime()) ? null : this.normalizeDateOnly(date);
    }

    // mm/dd/yyyy
    const us = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (us) {
      const m = parseInt(us[1], 10) - 1;
      const d = parseInt(us[2], 10);
      const y = parseInt(us[3], 10);
      const date = new Date(y, m, d);
      return isNaN(date.getTime()) ? null : this.normalizeDateOnly(date);
    }

    const fallback = new Date(s);
    return isNaN(fallback.getTime()) ? null : this.normalizeDateOnly(fallback);
  }

  /**
   * Auto-correct each From/To date pair:
   * if From > To, set To = From; otherwise keep To unchanged.
   */
  private enforceDateRangeOrder(values: any): any {
    if (!values || this.dateRangePairs.length === 0) {
      return values;
    }

    let nextValues = values;
    this.dateRangePairs.forEach(({ fromKey, toKey }) => {
      const fromRaw = values[fromKey];
      const toRaw = values[toKey];
      const fromDate = this.parseDateLike(fromRaw);
      const toDate = this.parseDateLike(toRaw);
      if (!fromDate) return;
      // Auto-fix when To is missing/invalid OR To is before From.
      if (toDate && fromDate.getTime() <= toDate.getTime()) return;

      this.filterForm.get(toKey)?.setValue(fromRaw, { emitEvent: false });
      if (nextValues === values) {
        nextValues = { ...values };
      }
      nextValues[toKey] = fromRaw;
    });

    return nextValues;
  }

  /**
   * Make From/To correction immediate in the UI (no debounce wait):
   * when From becomes greater than To, set To to From right away.
   */
  private setupImmediateDateRangeAutoFix(): void {
    this.dateRangeSubs.forEach((s) => s.unsubscribe());
    this.dateRangeSubs = [];
    this.dateRangePairs.forEach(({ fromKey, toKey }) => {
      const fromCtrl = this.filterForm.get(fromKey);
      if (!fromCtrl) return;
      const sub = fromCtrl.valueChanges.subscribe((fromRaw) => {
        const toRaw = this.filterForm.get(toKey)?.value;
        const fromDate = this.parseDateLike(fromRaw);
        const toDate = this.parseDateLike(toRaw);
        if (!fromDate) return;
        // Auto-fix when To is empty/invalid or less than From.
        if (toDate && fromDate.getTime() <= toDate.getTime()) return;
        this.filterForm.get(toKey)?.setValue(fromRaw, { emitEvent: false });
      });
      this.dateRangeSubs.push(sub);
    });
  }

  get displayFields(): FilterField[] {
    return this.config?.fields || this.fields;
  }

  get displayLayout(): string {
    return this.config?.layout || this.layout;
  }

  get displayColumns(): number {
    return this.config?.columns || this.columns;
  }

  get showResetButton(): boolean {
    return this.config?.showReset ?? this.showReset;
  }

  get showApplyButton(): boolean {
    return this.config?.showApply ?? this.showApply;
  }

  get displayApplyLabel(): string {
    return this.config?.applyLabel || this.applyLabel || 'Apply';
  }

  getLayoutClasses(): string {
    switch (this.displayLayout) {
      case 'vertical':
        return 'flex flex-col gap-3 sm:gap-4';
      case 'grid':
        return 'grid gap-3 sm:gap-4 items-end';
      case 'horizontal':
      default:
        return 'flex flex-col sm:flex-row flex-wrap items-end gap-3 sm:gap-4';
    }
  }

  getFieldClasses(field?: FilterField): string {
    switch (this.displayLayout) {
      case 'vertical':
        return 'w-full';
      case 'grid':
        let classes = 'w-full min-w-0';
        if (field?.type === 'practice-location') {
          classes += ' col-span-2';
        }
        // When using explicit grid placement, don't add col-span
        if (field?.config && (field.config.gridRow != null || field.config.gridColumn != null)) {
          // placement handled by getGridPlacementStyles
        }
        if (field?.config?.class) {
          classes += ' ' + String(field.config.class);
        }
        return classes;
      case 'horizontal':
      default:
        // Allow per-field override via field.config.class
        const base = 'w-full sm:flex-1 sm:min-w-[200px]';
        if (field && field.config && field.config.class) {
          return `${base} ${field.config.class}`;
        }
        return base;
    }
  }

  /** Grid placement styles when config has gridRow/gridColumn/rowSpan/colSpan (for 4-row reference layout). */
  getGridPlacementStyles(field: FilterField): { [key: string]: string } {
    const c = field?.config;
    if (!c || (c.gridRow == null && c.gridColumn == null)) return {};
    const styles: { [key: string]: string } = {};
    if (c.gridRow != null) {
      styles['grid-row'] = c.rowSpan != null ? `${c.gridRow} / span ${c.rowSpan}` : String(c.gridRow);
    }
    if (c.gridColumn != null) {
      styles['grid-column'] = c.colSpan != null ? `${c.gridColumn} / span ${c.colSpan}` : String(c.gridColumn);
    }
    return styles;
  }

  /** When using explicit grid placement, put the Apply button in the last row (e.g. row 4). */
  getGridButtonRow(): string | null {
    if (this.displayLayout !== 'grid' || !this.config?.gridRows || this.config?.actionButtonsGrid) return null;
    return String(this.config.gridRows);
  }

  /** Grid placement for the shared Apply/Reset block when `actionButtonsGrid` is set. */
  getApplyResetButtonGridStyle(): { [key: string]: string } {
    if (this.displayLayout !== 'grid') return {};
    const ag = this.config?.actionButtonsGrid;
    if (ag) {
      const styles: { [key: string]: string } = { 'grid-row': String(ag.gridRow) };
      if (ag.colSpan != null && ag.colSpan > 1) {
        styles['grid-column'] = `${ag.gridColumn} / span ${ag.colSpan}`;
      } else {
        styles['grid-column'] = String(ag.gridColumn);
      }
      return styles;
    }
    const row = this.getGridButtonRow();
    return row ? { 'grid-row': row } : {};
  }

  isMultiSelectField(field: FilterField): boolean {
    return field.type === 'multi-select';
  }


  getGridColumns(): string {
    return `grid-cols-${this.displayColumns}`;
  }

  onReset(): void {
    if (this.resetLoading || this.applyLoading) {
      return;
    }
    this.isResetting = true;

    // Reset form to current default values from config
    const resetValues: any = {};
    const fields = this.config?.fields || this.fields;
    
    fields.forEach(field => {
      if (field.type === 'reset-button' || field.type === 'find-button' || field.type === 'find-reset-buttons') return;
      const defaultValue = this.getFieldDefaultValue(field);
      if (field.type === 'date-time') {
        resetValues[field.key + '_date'] = '';
        resetValues[field.key + '_time'] = '';
      } else if (field.type === 'practice-location') {
        resetValues[field.key + '_location'] = field.config?.locationDefaultValue || '';
        resetValues[field.key + '_practice'] = field.config?.practiceDefaultValue || '';
      } else {
        resetValues[field.key] = defaultValue;
      }
    });
    
    this.filterForm.reset(resetValues);
    this.clearSearchableFields();
    this.filterForm.markAsPristine();
    this.filterForm.markAsUntouched();
    this.filterReset.emit();
    setTimeout(() => {
      this.isResetting = false;
    }, 0);
  }

  onFind(): void {
    if (this.findLoading) return;
    this.filterFind.emit();
  }

  onInputFieldEdit(field: FilterField): void {
    if (field.config?.showEditIcon) {
      this.filterFieldEdit.emit(field.key);
    }
  }

  onApply(): void {
    if (this.applyLoading || this.resetLoading) {
      return;
    }
    // Mark all required fields as touched to show validation errors
    Object.keys(this.filterForm.controls).forEach(key => {
      const control = this.filterForm.get(key);
      if (control && control.invalid) {
        control.markAsTouched();
      }
    });

    // Emit normalized value so From/To pairs are always valid on apply.
    const normalizedValues = this.enforceDateRangeOrder(this.filterForm.value);
    this.filterApply.emit(this.sanitizeValuesForEmit(normalizedValues));
  }

  get isFormValid(): boolean {
    return this.filterForm.valid;
  }

  getFieldValue(key: string): any {
    return this.filterForm.get(key)?.value;
  }

  getSearchableDisplayLabel(field: FilterField): string | undefined {
    const remembered = this.searchableDisplayLabels[field.key];
    if (remembered && remembered.trim() !== '') {
      return remembered;
    }

    const value = this.filterForm.get(field.key)?.value;
    if (value == null || value === '') {
      return undefined;
    }

    const options = field.searchableOptions || [];
    const match = options.find((option) => {
      if (option.value === value) return true;
      const optionNum = Number(option.value);
      const valueNum = Number(value);
      return !isNaN(optionNum) && !isNaN(valueNum) && optionNum === valueNum;
    });

    return match?.displayLabel || match?.label;
  }

  onSearchableValueChange(field: FilterField, value: string | number | (string | number)[]): void {
    if (Array.isArray(value)) {
      return;
    }

    if (value == null || value === '') {
      delete this.searchableDisplayLabels[field.key];
      return;
    }

    const options = field.searchableOptions || [];
    const match = options.find((option) => {
      if (option.value === value) return true;
      const optionNum = Number(option.value);
      const valueNum = Number(value);
      return !isNaN(optionNum) && !isNaN(valueNum) && optionNum === valueNum;
    });

    if (match?.displayLabel || match?.label) {
      const label = (match.displayLabel || match.label).trim();
      if (label && !(label === String(value) && /^\d+$/.test(label))) {
        this.searchableDisplayLabels[field.key] = label;
      }
    }
  }

  setFieldValue(key: string, value: any): void {
    this.filterForm.get(key)?.setValue(value);
  }

  setSearchableDisplayLabel(key: string, label: string | null | undefined): void {
    if (!label || String(label).trim() === '') {
      delete this.searchableDisplayLabels[key];
      return;
    }
    const trimmed = String(label).trim();
    const controlValue = this.filterForm.get(key)?.value;
    if (
      controlValue != null &&
      controlValue !== '' &&
      trimmed === String(controlValue) &&
      /^\d+$/.test(trimmed)
    ) {
      delete this.searchableDisplayLabels[key];
      return;
    }
    this.searchableDisplayLabels[key] = trimmed;
  }

  setSearchableSelection(key: string, value: any, label: string | null | undefined, emitEvent: boolean = false): void {
    this.setSearchableDisplayLabel(key, label);
    // Update child [displayLabelForValue] before writeValue runs on setValue.
    try {
      this.cdr.detectChanges();
    } catch {
      // Ignore if the component is already destroyed during async updates.
    }
    this.filterForm.get(key)?.setValue(value, { emitEvent });
    queueMicrotask(() => {
      try {
        this.cdr.detectChanges();
      } catch {
        // Ignore if the component is already destroyed during async updates.
      }
    });
  }

  onSearchQuery(fieldKey: string, searchTerm: string): void {
    this.searchQuery.emit({ fieldKey, searchTerm: (searchTerm ?? '').trim() });
  }

  onSearchableDropdownFocus(fieldKey: string): void {
    this.searchableDropdownFocus.emit(fieldKey);
  }
}
